import {
  Autocomplete,
  Box,
  createFilterOptions,
  debounce,
  Grid,
  List,
  ListItem,
  ListSubheader,
  TextField,
  Typography,
} from "@mui/material";
import { DateRangePicker } from "@mui/x-date-pickers-pro";
import { format, getTime } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  customerOrgAndCompAutocomplete,
  WorkLocation,
  workLocationsAutoCompleteQuery,
} from "../../customer/customerAPI";
import { EezyCompany } from "../../invoices/invoiceAPI";

import { isFulfilled } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import AutocompleteChip from "../../../common/components/functional/customAutocomplete/AutocompleteChip";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import CustomerAutocompleteField from "../../../common/components/functional/fields/customerAutocompleteField/CustomerAutocompleteField";
import ExpertsAutocomplete from "../../../common/components/functional/fields/expertsAutocomplete/ExpertsAutocomplete";
import { AutocompleteSelectField } from "../../../common/components/functional/fields/selectField/AutocompleteSelectField";
import { SearchFilter } from "../../../common/components/functional/FilterSearchField/FilterSearchField";
import {
  convertBooleanToYesNoValue,
  convertYesNoToBooleanParam,
  idsToNameIdOptions,
  makeLookupValues,
  makeMultiSelectDisplay,
} from "../../../common/helpers/filters";
import { leadingZerosDateFormat } from "../../../common/helpers/formatDateTime";
import useTesAutocompleteQuery from "../../../hooks/useTesAutocompleteQuery";
import { fetchEezyCompanies } from "../../invoices/invoiceSlice";
import { SCHEDULING_PERIOD_TYPES } from "../../settings/helpers/utils";
import { ENTRIES_APPROVAL_TYPES, ENTRIES_STATUS_TYPES } from "../constants";
import { Entry } from "../entriesAPI";
import { Approval, CustomerOptions, GroupKey, SelectedFilters } from "../types";
import { FilterItem } from "./FilterItem";
import GroupFields from "./GroupFields";
import { SelectAutocompleteMulti } from "../../../common/components/functional/SelectAutocomplete/SelectAutocompleteMulti";
import { SALARY_TYPE_OPTIONS } from "../../../common/constants/SalaryTypes";

interface Props {
  groups: GroupKey[];
  filters: SearchFilter[];
  updateFilters: (filters: any) => void;
  handleMenuToggle: (val?: boolean) => void;
  handleGrouping: (value: GroupKey[]) => void;
  selectedEntries: Entry[];
  setSelectedEntries: (val: Entry[]) => void;
  handleTabChangeByFilters: (newValue: string | [], key: string) => void;
}

const EntriesFilters = ({
  groups,
  filters,
  updateFilters,
  handleMenuToggle,
  handleGrouping,
  setSelectedEntries,
  handleTabChangeByFilters,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["common", "hourEntries"]);
  const { tes } = useAppSelector((state) => state.localized);
  const { setTesSearchTerm, tesIdsQueryResult } = useTesAutocompleteQuery();

  const defaultValues: SelectedFilters = {
    rangeStart: null,
    rangeEnd: null,
    paymentPeriod: null,
    status: "",
    customerOrg: null,
    customerCompany: null,
    eezyCompany: null,
    isOnInvoice: "",
    isOnPayslip: "",
    approval: "",
    isZeroExpenseEntry: "",
    tesId: "",
    hasComment: "",
    workLocations: [],
    hrRepresentatives: [],
    salaryTypes: [],
  };
  const methods = useForm<SelectedFilters>({
    defaultValues,
    mode: "onChange",
  });
  const { control, handleSubmit, setValue, watch } = methods;

  const [customerCompanySearchString, setCustomerCompanySearchString] =
    useState("");
  const [customerOptions, setCustomerOptions] = useState<CustomerOptions>({
    companies: [],
    organizations: [],
  });
  const [eezyCompanySearchString, setEezyCompanySearchString] = useState("");
  const [eezyCompanyOptions, setEezyCompanyOptions] = useState<EezyCompany[]>(
    [],
  );
  const [workLocationSearchString, setWorkLocationSearchString] = useState("");
  const [workLocationOptions, setWorkLocationOptions] = useState<
    WorkLocation[]
  >([]);

  const [selectedGroups, setSelectedGroups] = useState<GroupKey[]>(groups);
  const [changedFilterValue, setChangedFilterValue] = useState({
    key: "",
    value: "",
  });

  // Handle data from filters object to form state
  useEffect(() => {
    const filterObject: {
      [name: string]: {
        value: any;
        displayName?: string;
        displayValue?: string;
        lookupValues?: { id: string; name: string }[];
      };
    } = filters.reduce((acc: any, filter) => {
      const { name, value, displayName, displayValue, lookupValues } = filter;
      return {
        ...acc,
        [name]: {
          value: value.toString(),
          displayName,
          displayValue: displayValue?.toString(),
          lookupValues,
        },
      };
    }, {});
    const {
      rangeStart,
      rangeEnd,
      paymentPeriod,
      status,
      customerOrgId,
      customerCompanyId,
      eezyCompanyId,
      isOnInvoice,
      isOnPayslip,
      approval,
      isZeroExpenseEntry,
      tesId,
      hasComment,
      workLocationIds,
      hrRepresentativeIds,
      salaryTypes,
    } = filterObject;

    const workLocationsValue = workLocationIds?.value
      ? workLocationIds.value.split(",")
      : [];
    const workLocationsDisplayValue = workLocationIds?.displayValue?.split(",");

    methods.reset({
      rangeStart: rangeStart.value ? new Date(Number(rangeStart.value)) : null,
      rangeEnd: rangeEnd.value ? new Date(Number(rangeEnd.value)) : null,
      paymentPeriod: paymentPeriod.value
        ? SCHEDULING_PERIOD_TYPES.find(
            (sp) => sp.id.toString() === paymentPeriod.value,
          ) || null
        : null,
      status: status ? status.value : "",
      customerOrg: customerOrgId.value
        ? {
            id: customerOrgId.value,
            name: customerOrgId.displayValue || "",
          }
        : null,
      customerCompany: customerCompanyId.value
        ? {
            id: customerCompanyId.value,
            name: customerCompanyId.displayValue || "",
          }
        : null,
      eezyCompany: eezyCompanyId.value
        ? {
            eezyCompanyId: eezyCompanyId.value,
            name: eezyCompanyId.displayValue || "",
          }
        : null,
      isOnInvoice: convertBooleanToYesNoValue(isOnInvoice.value),
      isOnPayslip: convertBooleanToYesNoValue(isOnPayslip.value),
      approval: approval.value ? approval.value : "",
      isZeroExpenseEntry: convertBooleanToYesNoValue(isZeroExpenseEntry.value),
      tesId: tesId.value ? tesId.value : "",
      hasComment: convertBooleanToYesNoValue(hasComment.value),
      workLocations: workLocationIds?.value
        ? workLocationsValue.map((id: string, index: number) => ({
            id,
            name: workLocationsDisplayValue
              ? workLocationsDisplayValue[index]
              : [],
          }))
        : [],
      hrRepresentatives: hrRepresentativeIds?.value
        ? idsToNameIdOptions(
            hrRepresentativeIds.value.split(",").filter(Boolean),
            hrRepresentativeIds.lookupValues,
          )
        : [],
      salaryTypes: salaryTypes?.value
        ? idsToNameIdOptions(
            salaryTypes.value.split(",").filter(Boolean),
            salaryTypes.lookupValues,
            SALARY_TYPE_OPTIONS,
          )
        : [],
    });
  }, [filters, methods]);

  // Compose data that is passed on to filter chips and sent in fetchEntries
  const composeSearchFilters = (values: SelectedFilters) => {
    const {
      displayValue: hrRepresentativesDisplayValue,
      tooltip: hrRepresentativesTooltip,
    } = makeMultiSelectDisplay(values.hrRepresentatives || []);
    const {
      displayValue: salaryTypesDisplayValue,
      tooltip: salaryTypesTooltip,
    } = makeMultiSelectDisplay(values.salaryTypes || []);

    return [
      {
        name: "rangeStart",
        value: values.rangeStart ? getTime(values.rangeStart) : "",
        displayValue: values.rangeStart
          ? format(values.rangeStart, leadingZerosDateFormat)
          : "",
        type: "date",
      },
      {
        name: "rangeEnd",
        value: values.rangeEnd ? getTime(values.rangeEnd) : "",
        displayValue: values.rangeEnd
          ? format(values.rangeEnd, leadingZerosDateFormat)
          : "",
        type: "date",
      },
      {
        name: "paymentPeriod",
        value: values.paymentPeriod ? values.paymentPeriod.id : "",
        displayValue: values.paymentPeriod
          ? `schedulingPeriods:${values.paymentPeriod.name}`
          : "",
      },
      { name: "status", value: values.status },
      {
        name: "isOnInvoice",
        value: convertYesNoToBooleanParam(values.isOnInvoice),
        displayName: "onInvoice",
        displayValue: `common:${values.isOnInvoice}`,
      },
      { name: "approval", value: values.approval },
      {
        name: "isOnPayslip",
        value: convertYesNoToBooleanParam(values.isOnPayslip),
        displayName: "onPayslip",
        displayValue: `common:${values.isOnPayslip}`,
      },
      {
        name: "customerOrgId",
        value: values.customerOrg?.id || "",
        displayValue: values.customerOrg?.name || "",
      },
      {
        name: "customerCompanyId",
        value: values.customerCompany?.id || "",
        displayValue: values.customerCompany?.name || "",
      },
      {
        name: "eezyCompanyId",
        value: values.eezyCompany?.eezyCompanyId || "",
        displayName: "eezyCompany",
        displayValue: values.eezyCompany?.name || "",
      },
      {
        name: "isZeroExpenseEntry",
        value: convertYesNoToBooleanParam(values.isZeroExpenseEntry),
        displayName: "zeroExpenseEntry",
        displayValue: `common:${values.isZeroExpenseEntry}`,
      },
      {
        name: "tesId",
        value: values.tesId,
        displayName: "common:tes",
        displayValue: tes[values.tesId],
      },
      {
        name: "hasComment",
        value: convertYesNoToBooleanParam(values.hasComment),
        displayName: "comment",
        displayValue: `common:${values.hasComment}`,
      },
      {
        name: "workLocationIds",
        value: values.workLocations?.map((wl) => wl.id),
        displayName: "common:workLocation",
        displayValue:
          values.workLocations?.map((wl) => wl.name).join(", ") || [],
      },
      {
        name: "hrRepresentativeIds",
        value: values.hrRepresentatives?.map((hr) => hr.id) || [],
        displayName: "talentProfile:myHr",
        displayValue: hrRepresentativesDisplayValue,
        ...(hrRepresentativesTooltip && { tooltip: hrRepresentativesTooltip }),
        lookupValues: makeLookupValues(values.hrRepresentatives || []),
      },
      {
        name: "salaryTypes",
        value: values.salaryTypes?.map((st) => st.id) || [],
        displayName: "talentProfile:salaryTypeLabel",
        displayValue: salaryTypesDisplayValue,
        ...(salaryTypesTooltip && { tooltip: salaryTypesTooltip }),
        lookupValues: makeLookupValues(values.salaryTypes || []),
      },
    ];
  };

  const sendFilters = (formValues: SelectedFilters) => {
    handleGrouping(selectedGroups);
    const filters = composeSearchFilters(formValues);
    updateFilters(filters);
    setSelectedEntries([]);
    handleMenuToggle(false);
  };

  const handleCustomerSearchStringChange = debounce((value: string) => {
    setCustomerCompanySearchString(value);
  }, 500);

  const handleEezyCompanySearchStringChange = debounce(
    (value: string) => setEezyCompanySearchString(value),
    500,
  );

  const dispatchEezyCompanySearch = useCallback(
    (searchString: string) => {
      dispatch(fetchEezyCompanies({ name: searchString })).then(
        (action) =>
          isFulfilled(action) && setEezyCompanyOptions(action.payload),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (eezyCompanySearchString !== "") {
      dispatchEezyCompanySearch(eezyCompanySearchString);
    }
  }, [eezyCompanySearchString, dispatchEezyCompanySearch]);

  const customerCompanyFilterOptions = useMemo(() => {
    return customerOptions.companies.map((item) => ({
      id: item.id,
      name: item.name,
    }));
  }, [customerOptions]);

  useEffect(() => {
    dispatch(fetchEezyCompanies({})).then(
      (action) => isFulfilled(action) && setEezyCompanyOptions(action.payload),
    );
  }, [dispatch]);

  const handleWorkLocationSearchStringChange = debounce(
    (value: string) => setWorkLocationSearchString(value),
    500,
  );

  const dispatchWorkLocationSearch = (searchString: string) => {
    workLocationsAutoCompleteQuery({ term: searchString }).then((res) => {
      const sortedOptions = res.sort(
        (a, b) =>
          a.customerOrgName?.localeCompare(b.customerOrgName || "") ||
          a.name.localeCompare(b.name),
      );
      setWorkLocationOptions(sortedOptions);
    });
  };

  useEffect(() => {
    if (workLocationSearchString !== "") {
      dispatchWorkLocationSearch(workLocationSearchString);
    }
  }, [workLocationSearchString]);

  const dispatchCustomerOrgSearch = useCallback(
    (searchString: string) => {
      try {
        customerOrgAndCompAutocomplete({ name: searchString }).then((res) => {
          setCustomerOptions({
            ...customerOptions,
            companies: res,
          });

          setCustomerCompanySearchString("");
        });
      } catch (e: any) {
        console.error("Failed to fetch Customer companies: ", e);
      }
    },
    [customerOptions],
  );

  useEffect(() => {
    if (customerCompanySearchString !== "") {
      dispatchCustomerOrgSearch(customerCompanySearchString);
    }
  }, [customerCompanySearchString, dispatchCustomerOrgSearch]);

  const updateApproval = (newValue: string) => {
    handleTabChangeByFilters(newValue, "approval");
    // Change filter value after tab change
    setChangedFilterValue({ key: "approval", value: newValue });
  };

  const updateStatus = (newValue: string) => {
    handleTabChangeByFilters(newValue, "status");
    // Change filter value after tab change
    setChangedFilterValue({ key: "status", value: newValue });
  };

  useEffect(() => {
    switch (changedFilterValue.key) {
      case "approval":
        return setValue("approval", changedFilterValue.value as Approval);
      case "status":
        return setValue("status", changedFilterValue.value);
      default:
        break;
    }
  }, [changedFilterValue, setValue]);

  const { rangeStart, rangeEnd } = watch();
  const hasValidDateFilter = !!rangeStart && !!rangeEnd;

  return (
    <Box sx={{ marginTop: "20px" }}>
      <FormProvider {...methods}>
        <Grid container spacing={4} alignItems="flex-start">
          <Grid item container xs={6} rowGap={1}>
            <FilterItem
              label={t("hourEntries:groupBy")}
              labelVerticalAlign="top"
            >
              <GroupFields
                selectedGroups={selectedGroups}
                setSelectedGroups={setSelectedGroups}
              />
            </FilterItem>
            <FilterItem label={t("common:date")}>
              {/* TODO refactor */}
              <Controller
                name="rangeStart"
                control={control}
                render={({ field: { value } }) => (
                  <DateRangePicker
                    format={leadingZerosDateFormat}
                    value={[value, control._formValues.rangeEnd]}
                    onChange={([start, end]) => {
                      setValue("rangeStart", start);
                      setValue("rangeEnd", end);
                    }}
                    slotProps={{
                      textField: { size: "small", label: null },
                      fieldSeparator: { children: "-" },
                    }}
                  />
                )}
              />
            </FilterItem>
            <FilterItem label={t("hourEntries:paymentPeriod")}>
              <Controller
                name="paymentPeriod"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    value={value}
                    options={SCHEDULING_PERIOD_TYPES}
                    getOptionLabel={(option) =>
                      t(`schedulingPeriods:${option.name}`)
                    }
                    renderInput={(params) => (
                      <CommonTextField
                        {...params}
                        size="small"
                        name="paymentPeriod"
                      />
                    )}
                    onChange={(_, newValue) => onChange(newValue)}
                  />
                )}
              />
            </FilterItem>
            <FilterItem label={t("hourEntries:eezyCompany")}>
              <Controller
                name="eezyCompany"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    value={value || null}
                    getOptionLabel={(option) => option?.name || ""}
                    noOptionsText={t("common:noOptions")}
                    options={eezyCompanyOptions}
                    isOptionEqualToValue={(option, value) =>
                      option?.eezyCompanyId === value?.eezyCompanyId
                    }
                    filterOptions={createFilterOptions({
                      stringify: (option) => JSON.stringify(option),
                    })}
                    renderOption={(props, option) => (
                      <React.Fragment key={option.eezyCompanyId}>
                        <ListItem {...props}>{option.name}</ListItem>
                      </React.Fragment>
                    )}
                    renderInput={(params) => (
                      <CommonTextField
                        {...params}
                        size="small"
                        name="eezyCompany"
                        required
                      />
                    )}
                    onChange={(_, newValue) => onChange(newValue)}
                    onInputChange={(_, newValue) => {
                      if (newValue) {
                        handleEezyCompanySearchStringChange(newValue);
                      }
                    }}
                  />
                )}
              />
            </FilterItem>
            <FilterItem label={t("hourEntries:customerCompanyId")}>
              <Controller
                name="customerCompany"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    value={value}
                    getOptionLabel={(option) => option?.name || ""}
                    noOptionsText={t("common:writeToSearch")}
                    options={customerCompanyFilterOptions}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    filterOptions={createFilterOptions({
                      stringify: (option) => JSON.stringify(option),
                    })}
                    renderOption={(props, option) => (
                      <React.Fragment key={option.id}>
                        <ListItem {...props}>{option.name}</ListItem>
                      </React.Fragment>
                    )}
                    renderInput={(params) => (
                      <CommonTextField
                        {...params}
                        size="small"
                        name="customerCompany"
                        required
                      />
                    )}
                    onChange={(_, newValue) => onChange(newValue)}
                    onInputChange={(_, newValue) => {
                      if (newValue) {
                        handleCustomerSearchStringChange(newValue);
                      }
                    }}
                  />
                )}
              />
            </FilterItem>
            <FilterItem label={t("common:customerOrganizationName")}>
              <CustomerAutocompleteField name="customerOrg" includeCompanies />
            </FilterItem>
            <FilterItem label={t("workLocation")}>
              <Controller
                name="workLocations"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    multiple
                    value={value || []}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    options={workLocationOptions}
                    filterOptions={createFilterOptions({
                      stringify: (option) => JSON.stringify(option),
                    })}
                    onChange={(_, newValue) => onChange(newValue)}
                    onInputChange={(_, newValue) => {
                      if (newValue) {
                        handleWorkLocationSearchStringChange(newValue);
                      }
                    }}
                    limitTags={2}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <AutocompleteChip
                          {...getTagProps({ index })}
                          labelSx={{ minWidth: "145px" }}
                          label={option.name}
                        />
                      ))
                    }
                    groupBy={(option) => option.name}
                    renderGroup={({ group, children }) => (
                      <>
                        <ListSubheader
                          disableSticky
                          sx={{
                            color: "#9167CA",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            lineHeight: "20px",
                          }}
                        >
                          <Typography variant="header">{group}</Typography>
                        </ListSubheader>
                        <List sx={{ paddingLeft: "32px" }}>{children}</List>
                      </>
                    )}
                    renderInput={(params) => (
                      <CommonTextField
                        {...params}
                        size="small"
                        name="workLocation"
                        required
                      />
                    )}
                  />
                )}
              />
            </FilterItem>
            <Grid item container spacing={2} xs={12} alignItems="center">
              <Grid item xs={12} mt={2}>
                <CustomButton
                  disabled={!hasValidDateFilter}
                  sx={{ borderRadius: "20px", minHeight: "42px" }}
                  variant="contained"
                  onClick={handleSubmit((data) => sendFilters(data))}
                >
                  {t("hourEntries:getHourEntries")}
                </CustomButton>
              </Grid>
            </Grid>
          </Grid>
          <Grid item container xs={6} rowGap={1}>
            <FilterItem label={t("hourEntries:status")}>
              <Controller
                name="status"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    value={value}
                    options={ENTRIES_STATUS_TYPES}
                    getOptionLabel={(option) => t(`hourEntries:${option}`)}
                    renderInput={(params) => (
                      <TextField {...params} size="small" />
                    )}
                    onChange={(_, newValue) => updateStatus(newValue || "")}
                  />
                )}
              />
            </FilterItem>
            <FilterItem label={t("hourEntries:onInvoice")}>
              <AutocompleteSelectField name="isOnInvoice" />
            </FilterItem>
            <FilterItem label={t("hourEntries:onPayslip")}>
              <AutocompleteSelectField name="isOnPayslip" />
            </FilterItem>
            <FilterItem label={t("hourEntries:approval")}>
              <Controller
                name="approval"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    value={value}
                    options={ENTRIES_APPROVAL_TYPES}
                    getOptionLabel={(option) => t(`hourEntries:${option}`)}
                    renderInput={(params) => (
                      <TextField {...params} size="small" />
                    )}
                    onChange={(_, newValue) => updateApproval(newValue || "")}
                  />
                )}
              />
            </FilterItem>
            <FilterItem label={t("hourEntries:zeroExpenseEntry")}>
              <AutocompleteSelectField name="isZeroExpenseEntry" />
            </FilterItem>
            <FilterItem label={t("common:tes")}>
              <Controller
                name="tesId"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    sx={{ maxWidth: "372px" }}
                    value={value}
                    options={tesIdsQueryResult}
                    noOptionsText={t("common:writeToSearch")}
                    getOptionLabel={(tesId) => tesId && tes[tesId]}
                    onInputChange={(event, newInput) => {
                      if (event?.type === "change") {
                        setTesSearchTerm(newInput);
                      }
                    }}
                    onChange={(_, newValue) =>
                      onChange(newValue ? newValue : "")
                    }
                    renderInput={(params) => <CommonTextField {...params} />}
                  />
                )}
              />
            </FilterItem>
            <FilterItem label={t("talentProfile:comment")}>
              <AutocompleteSelectField name="hasComment" />
            </FilterItem>
            <FilterItem label={t("talentProfile:myHr")}>
              <Controller
                name="hrRepresentatives"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ExpertsAutocomplete
                    value={value || []}
                    onChange={(newValue) => onChange(newValue || [])}
                    includeNoHrRep
                    limitTags={2}
                  />
                )}
              />
            </FilterItem>
            <FilterItem label={t("talentProfile:salaryTypeLabel")}>
              <Controller
                name="salaryTypes"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <SelectAutocompleteMulti
                    value={value}
                    onChange={(newValue) => onChange(newValue || [])}
                    options={SALARY_TYPE_OPTIONS.map((option) => ({
                      id: option.id,
                      name: t(option.name),
                    }))}
                    translationNamespaces={["talentProfile"]}
                  />
                )}
              />
            </FilterItem>
          </Grid>
        </Grid>
      </FormProvider>
    </Box>
  );
};

export default EntriesFilters;
